/**
 * Utility Functions for WebSocket Session Hooks
 * 
 * Helper functions used across the refactored hooks.
 */

// ============================================================================
// Audio Utilities
// ============================================================================

/**
 * Create WAV blob from PCM16 data for debugging
 * 
 * @param pcm16Data - Raw PCM16 audio data
 * @param sampleRate - Sample rate (typically 24000 for OpenAI)
 * @param numChannels - Number of channels (typically 1 for mono)
 * @returns WAV Blob
 */
export function createWavBlob(pcm16Data: Uint8Array, sampleRate: number, numChannels: number): Blob {
  const dataLength = pcm16Data.length;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);
  
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  // RIFF header
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  
  // fmt chunk
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // chunk size
  view.setUint16(20, 1, true); // audio format (PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true); // byte rate
  view.setUint16(32, numChannels * 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  
  // data chunk
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);
  
  // Audio data
  new Uint8Array(buffer, 44).set(pcm16Data);
  
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Combine multiple audio chunks into a single Uint8Array
 * 
 * @param chunks - Array of audio chunks
 * @returns Combined Uint8Array
 */
export function combineAudioChunks(chunks: Uint8Array[]): Uint8Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }
  return combined;
}

